import type { CSSProperties } from 'react'
import clsx from 'clsx'
import type { ISODate, InterestSave } from '@app/shared'

import { PlayFrame } from './icons'
import { SOURCE_RULE, SourceTile } from './parts'
import { durationLabel, savedAgo } from './labels'

// ============================================================================
// ONE SAVE, IN THE LIBRARY GRID.
//
// The card carries four facts and no more: where it came from, what it says,
// how old it is, and whether it has been read. Everything else — the excerpt,
// the AI two-liner, the note, the neighbours — waits for the detail pane, which
// is what keeps a wall of fourteen cards scannable.
//
// READ IS A STATE, NOT AN ABSENCE. The dot is filled and blue while unread and
// neutral once read; it is never simply missing, because a missing dot and a
// read dot would look the same and mean different things.
//
// A VIDEO SAVE GETS A MEDIA BAND, and the band is a gradient with a play glyph
// rather than a thumbnail: this bundle ships no bitmaps (README §Assets), and a
// grey box pretending a thumbnail is still loading would be a lie that never
// resolves.
//
// THE WHOLE CARD IS THE CONTROL, and it is a real `<button>` rather than a div
// with a click handler: keyboard reaches it, the focus ring lands on it, and its
// accessible name is the four facts it draws. README §6 says what pressing it
// does — "Selecting a save opens a right-hand detail pane" — so this opens the
// pane and never the original link. `Open original` exists once, in that pane,
// where the mockup draws it; a second one here would put the destructive-ish act
// (leaving the app) on the surface the eye sweeps.
//
// MOTION (index.css → THE MOTION SYSTEM): `.mo-ink` for hover, `.mo-press` for
// press, and the one knob they take — `--mo-rule` — set to the SAVE'S SOURCE
// hue, so hovering a Reddit save washes it faintly orange and a YouTube save
// red. The mechanic is chosen by radius, and the card is radius 14: the same
// bucket the calendar's radius-14 pills already take (`.mo-ink`, not `.mo-rule`,
// whose straight inset rule is specified for radius ≤ 10).
// ============================================================================

export function SaveCard({
  save,
  today,
  selected,
  onSelect,
}: {
  save: InterestSave
  today: ISODate
  selected: boolean
  onSelect: () => void
}) {
  const media = save.source === 'youtube'

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      style={{ '--mo-rule': SOURCE_RULE[save.source] } as CSSProperties}
      className={clsx(
        'mo-ink mo-press flex flex-col overflow-hidden rounded-card bg-surface text-left',
        media ? '' : 'gap-3 px-[17px] py-4',
        selected ? 'border-[1.5px] border-ink shadow-save' : 'border border-line',
      )}
    >
      {media && (
        <span className="relative flex h-[86px] flex-none items-center justify-center bg-[linear-gradient(120deg,var(--color-media-from),var(--color-media-to))]">
          <PlayFrame size={26} className="text-muted" />
          {save.durationSec !== null && (
            <span className="absolute bottom-[7px] right-2 rounded-[5px] bg-badge-scrim px-[6px] text-[9.5px] font-semibold leading-[1.7] text-on-dark">
              {durationLabel(save.durationSec)}
            </span>
          )}
        </span>
      )}

      <span className={clsx('flex flex-col gap-[11px]', media && 'px-[17px] pb-4 pt-[13px]')}>
        <span className="flex items-center gap-2">
          <SourceTile source={save.source} />
          <span className="truncate text-[11px] font-semibold leading-none text-ink-3">
            {save.sourceDetail}
          </span>
          <span className="ml-auto flex flex-none items-center gap-[7px]">
            {save.note !== null && (
              <span className="rounded-pill bg-warning-fill px-[7px] text-[9.5px] font-semibold leading-[1.7] text-warning-2">
                1 note
              </span>
            )}
            <span
              className={clsx(
                'size-[7px] flex-none rounded-full',
                save.unread ? 'bg-unread-dot' : 'bg-line-2',
              )}
              aria-hidden
            />
          </span>
        </span>

        <span className="text-[14px] font-semibold leading-[1.45] text-pretty">{save.title}</span>

        <span className="flex items-center gap-2 text-[10.5px] leading-none text-microcopy">
          <span>saved {savedAgo(save.savedAt, today)}</span>
          <span className="size-[3px] flex-none rounded-full bg-line-2" aria-hidden />
          <span>{save.unread ? 'unread' : 'read'}</span>
        </span>
      </span>
    </button>
  )
}
